import React from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import { AlertsTableColumn } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import { RecipientKind } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import {
  ALERTS_PILL_KEYS,
  ALERTS_RAW_ID_KEYS,
  alertsTone,
  formatAlertsCount,
  formatAlertsDateTime,
  formatAlertsText,
} from "./alertsDashboardFormat";

const CELL_FONT_PX = 11;
const CHIP_FONT_PX = 10;
const CELL_PADDING = "4px 8px";

/** Name columns that open the `/recipient` view (id key + recipient kind). */
const RECIPIENT_LINKS: Record<string, { idKey: string; kind: RecipientKind }> =
  {
    admin_name: { idKey: "admin_id", kind: "ADMIN" },
    user_name: { idKey: "user_id", kind: "CUSTOMER_USER" },
  };

interface AlertsDataTableProps {
  maxHeight: number;
  columns: AlertsTableColumn[];
  rows: Array<Record<string, unknown>>;
  pageNo: number;
  limit: number;
  totalCount: number;
  sortableColumns: readonly string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  onSort: (column: string) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
  onRecipientClick?: (
    kind: RecipientKind,
    id: number,
    name: string | null,
  ) => void;
}

const getColumnMinWidth = (column: AlertsTableColumn): string => {
  if (column.type === "datetime") {
    return "140px";
  }
  if (column.key === "title" || column.key === "last_error") {
    return "200px";
  }
  if (column.type === "number") {
    return "70px";
  }
  return "100px";
};

const getSerialNumber = (
  page: number,
  pageSize: number,
  index: number,
): number => {
  try {
    return (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + index + 1;
  } catch (error) {
    console.error(error);
    return index + 1;
  }
};

const rowKeyFor = (row: Record<string, unknown>, index: number): string => {
  try {
    const parts = [row.alert_id, row.batch_id, row.delivery_id, index];
    return parts.map((part) => String(part ?? "")).join("-");
  } catch (error) {
    console.error(error);
    return `row-${index}`;
  }
};

export const AlertsPill = ({ value }: { value: unknown }) => {
  const theme = useTheme();
  const raw = value == null || value === "" ? "" : String(value);
  if (!raw) {
    return <>-</>;
  }
  const tone = alertsTone(theme, raw);
  return (
    <Chip
      size="small"
      label={raw}
      sx={{
        height: 18,
        fontSize: `${CHIP_FONT_PX}px`,
        fontWeight: 600,
        bgcolor: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.fg}33`,
      }}
    />
  );
};

const formatRawId = (value: unknown): string => {
  if (value == null || value === "") {
    return "-";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  return String(value);
};

const AlertsDataTable: React.FC<AlertsDataTableProps> = ({
  maxHeight,
  columns,
  rows,
  pageNo,
  limit,
  totalCount,
  sortableColumns,
  sortBy,
  sortDir = "desc",
  onSort,
  onPageChange,
  onRowClick,
  onRecipientClick,
}) => {
  const theme = useTheme<Theme>();
  const headerColor = theme.palette.customColors.tableGrayHeadingColor;
  const sortable = new Set(
    sortableColumns.filter((key) =>
      columns.some((column) => column.key === key),
    ),
  );

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortDir === "asc" ? "↑" : "↓";
    }
    return "↑↓";
  };

  const renderCell = (
    column: AlertsTableColumn,
    row: Record<string, unknown>,
  ): React.ReactNode => {
    try {
      const value = row[column.key];
      const link = RECIPIENT_LINKS[column.key];
      if (link && onRecipientClick && typeof row[link.idKey] === "number") {
        const id = row[link.idKey] as number;
        const name = value == null ? null : String(value);
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onRecipientClick(link.kind, id, name);
            }}
            sx={{ fontSize: `${CELL_FONT_PX}px`, fontWeight: 600 }}
          >
            {name || `#${id}`}
          </Link>
        );
      }
      if (ALERTS_PILL_KEYS.has(column.key)) {
        return <AlertsPill value={value} />;
      }
      if (ALERTS_RAW_ID_KEYS.has(column.key)) {
        return formatRawId(value);
      }
      if (column.type === "datetime") {
        return formatAlertsDateTime(value);
      }
      if (column.type === "number") {
        if (typeof value === "number" && Number.isFinite(value)) {
          const formatted = formatAlertsCount(value);
          return column.unit ? `${formatted}${column.unit}` : formatted;
        }
        return "-";
      }
      return formatAlertsText(value);
    } catch (error) {
      console.error(error);
      return "-";
    }
  };

  const cellSx = {
    textAlign: "center" as const,
    fontSize: CELL_FONT_PX,
    padding: CELL_PADDING,
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead sx={{ position: "relative", zIndex: 30 }}>
            <TableRow>
              <AppTableHeaderCell sx={{ ...cellSx, minWidth: "52px" }}>
                #
              </AppTableHeaderCell>
              {columns.map((column) => {
                const canSort = sortable.has(column.key);
                return (
                  <AppTableHeaderCell
                    key={column.key}
                    sx={{ ...cellSx, minWidth: getColumnMinWidth(column) }}
                  >
                    {canSort ? (
                      <TableSortLabel
                        active={sortBy === column.key}
                        direction={sortDir === "asc" ? "asc" : "desc"}
                        onClick={() => {
                          try {
                            onSort(column.key);
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                        IconComponent={() => (
                          <span>{renderSortIcon(column.key)}</span>
                        )}
                        sx={{
                          fontSize: `${CELL_FONT_PX}px`,
                          color: headerColor,
                          "&.Mui-active": { color: headerColor },
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </AppTableHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const rowBg =
                index % 2 === 0
                  ? theme.palette.action.hover
                  : theme.palette.background.paper;
              return (
                <TableRow
                  key={rowKeyFor(row, index)}
                  hover
                  onClick={
                    onRowClick
                      ? () => {
                          try {
                            onRowClick(row);
                          } catch (error) {
                            console.error(error);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    backgroundColor: rowBg,
                    cursor: onRowClick ? "pointer" : "default",
                    "&:hover": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <AppTableRowCell sx={cellSx}>
                    {getSerialNumber(pageNo, limit, index)}
                  </AppTableRowCell>
                  {columns.map((column) => (
                    <AppTableRowCell key={`${column.key}-${index}`} sx={cellSx}>
                      <Box component="span" sx={{ wordBreak: "break-word" }}>
                        {renderCell(column, row)}
                      </Box>
                    </AppTableRowCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize={`${CELL_FONT_PX}px`}
        pageNo={pageNo}
        limit={limit}
        totalCount={totalCount}
        showRowsPerPageOptions={false}
        showLabelRowsPerPage={false}
        onPageChange={onPageChange}
        handleRowsChange={() => undefined}
      />
    </Paper>
  );
};

export default AlertsDataTable;
